import { Typography } from '@eventbrite/marmalade';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { AutocompleteVenue, AutocompleteVenueLocation } from '../../types/search';
import styles from './VenueResult.module.scss';

const getVenueLocationLabel = (locations: AutocompleteVenueLocation[]) => {
    const locality = locations.find((location) => location.type === 'locality');
    const region = locations.find((location) => location.type === 'region');
    const locationComponents = [locality?.name, region?.name].filter(Boolean);

    return locationComponents.join(', ');
};

const VenueIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <div className={styles.venueIconContainer}>
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={24}
            height={24}
            fill="none"
            {...props}
            className={styles.venueIcon}
        >
            <path
                fill="#585163"
                fillRule="evenodd"
                d="M12 23s9-7 9-13c0-5.5-4.03-9-9-9s-9 3.5-9 9c0 6 9 13 9 13Zm0-10a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                clipRule="evenodd"
            />
        </svg>
    </div>
);

const eventCountMessage = {
    id: '{eventCount} upcoming events',
    defaultMessage: '{eventCount} upcoming events',
};

const eventCountMessageSingle = {
    id: '1 upcoming event',
    defaultMessage: '1 upcoming event',
};

const venueLabelMessage = {
    id: 'Venue',
    defaultMessage: 'Venue',
};

export const VenueResult = ({ venue }: { venue: AutocompleteVenue }) => {
    const { formatMessage } = useIntl();

    let eventCountText = formatMessage(eventCountMessage, {
        eventCount: venue.event_count,
    });

    if (venue.event_count === 1) {
        eventCountText = formatMessage(eventCountMessageSingle);
    }

    const venueLabel = formatMessage(venueLabelMessage);

    const venueLocationLabel = useMemo(() => {
        return venue.locations?.length ? getVenueLocationLabel(venue.locations) : '';
    }, [venue.locations]);

    const venueDetailsText = venueLocationLabel
        ? `${venueLabel} • ${venueLocationLabel} • ${eventCountText}`
        : `${venueLabel} • ${eventCountText}`;

    return (
        <div className={styles.venueResult}>
            <VenueIcon />
            <div className={styles.venueDetailsContainer}>
                <Typography variant="body-md-bold" className={styles.venueName}>
                    {venue.venue_name}
                </Typography>
                <Typography variant="body-caption" className={styles.venueDetails}>
                    {venueDetailsText}
                </Typography>
            </div>
        </div>
    );
};
